import { CancelButton, SaveButton } from '@core/components/Buttons';
import { SimpleDialog, SimpleDialogContent } from '@core/components/Dialog';
import ImportErrorTable from '@core/components/ImportModal/ImportErrorTable';
import ImportModal from '@core/components/ImportModal/ImportModal';
import { useApiUtils } from '@core/util/apiUtils';
import { downloadFile } from '@core/util/downloadUtils';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import FuseUtils from '@fuse/utils';
import { ClienteContext } from 'app/main/cliente/ClienteContext';
import { clienteApi } from 'app/services/api/clienteApi';
import * as Actions from 'app/store/actions';
import { eventReactGA } from 'app/utils/analyticsUtil';
import { FormikProvider, useFormik } from 'formik';
import React, { useContext, useState } from 'react';
import { FaUsers } from 'react-icons/fa';
import { IoIosAlert } from 'react-icons/io';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { clienteModais } from '../clienteModais';

const CLIENTE = 'Cliente';

const ImportarClienteModal = ({ fetchClientes }) => {
  const [modal, setModal] = useContext(ClienteContext).modal;
  const [, setLoading] = useContext(ClienteContext).loading;
  const [arquivo, setArquivo] = useContext(ClienteContext).arquivo;
  const [search] = useContext(ClienteContext).search;
  const [size] = useContext(ClienteContext).size;
  const [sort] = useContext(ClienteContext).sort;
  const [page] = useContext(ClienteContext).page;
  const [error, setError] = useState(false);
  const [errorData, setErrorData] = useState([]);
  const [idNotificacao, setIdNotificacao] = useState();
  const [progress, setProgress] = useState(0);
  const [disabled, setDisabled] = useState(false);

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const extensoesPermitidas = ['xlsx'];

  const titulo = error ? 'Erros identificados na importação' : 'Importar planilha de clientes';

  const obterModelo = async () => {
    try {
      setLoading(true);
      eventReactGA(CLIENTE, 'Obter planilha');

      const response = await clienteApi.obterModelo();

      downloadFile(response.data.bytes, response.data.nome);
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (message, variant) => {
    dispatch(
      Actions.showMessage({
        message,
        variant,
      })
    );
  };

  const handleClose = () => {
    setArquivo('');
    setDisabled(false);
    if (error) {
      setError(false);
      setErrorData('');
    }
    if (!error) {
      setModal('');
      formik.resetForm(formik.initialValues);
    }
  };

  const handleUpload = (file) => {
    if (file) {
      try {
        const chaveNotificacao = FuseUtils.generateGUID();
        setIdNotificacao(chaveNotificacao);
        setDisabled(true);

        const arquivo = Object.assign(file, {
          id: undefined,
          nome: file.name,
          contentType: file.type,
        });

        const extensao = arquivo.nome.substring(arquivo.nome.lastIndexOf('.') + 1);

        if (!extensoesPermitidas.includes(extensao.toLowerCase())) {
          setArquivo('');
          showMessage('É permitido anexar somente arquivos no formato .xlsx', 'error');
          setLoading(false);
          setDisabled(false);
        } else {
          const formData = new FormData();
          formData.append('arquivo', arquivo);

          setLoading(true);
          eventReactGA(CLIENTE, 'Importar planilha');
          clienteApi.importar(formData, chaveNotificacao).catch((err) => {
            apiUtils.handleApiError(err);
            setLoading(false);
            setDisabled(false);
          });
        }
      } catch (err) {
        apiUtils.handleApiError(err);
      }
    }
  };

  const handleSubmit = () => {
    setLoading(true);
    handleUpload(arquivo);
  };

  const ImportarArquivoSchema = yup.object().shape({
    arquivo: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('o arquivo')),
  });

  const getInitialValues = (registro) => ({
    ...registro,
    id: registro?.id,
    arquivo: registro?.arquivo,
  });

  const formik = useFormik({
    initialValues: getInitialValues(arquivo),
    validationSchema: ImportarArquivoSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const onFileChange = (event) => {
    const file = event.target.files[0];
    setProgress(0);

    formik.handleChange({
      target: {
        id: 'arquivo',
        value: file,
      },
    });

    setArquivo(file);
  };

  const handleProgressUpdate = (notificacao) => {
    if (idNotificacao === notificacao.id) {
      const mensagem = JSON.parse(notificacao.mensagem);
      const newProgress = parseFloat(mensagem.percentual);
      setProgress(newProgress);
      setLoading(false);

      if (newProgress === 100) {
        if (mensagem?.errors && mensagem?.errors?.length > 0) {
          if (mensagem.errors[0].campo === 'arquivo-error') {
            showMessage(mensagem.errors[0].mensagemErro, 'error');
            setDisabled(false);
            setProgress(0);
          } else if (mensagem.errors[0].campo === 'arquivo-planilha-error') {
            showMessage(mensagem.errors[0].mensagemErro, 'error');
            setDisabled(false);
            setProgress(0);
          } else {
            setError(true);
            setErrorData(mensagem.errors);
          }
        }
        if (mensagem?.errors?.length === 0) {
          showMessage('Importação realizada com sucesso.', 'success');
          handleClose();
          fetchClientes({ q: search, page, size, sort });
        }
      }
    }
  };

  return (
    <SimpleDialog open={modal === clienteModais.importarCliente} onClose={handleClose} innerScroll>
      <FormikProvider value={formik}>
        <SimpleDialogContent
          header={{
            title: titulo,
            icon: error ? <IoIosAlert /> : <FaUsers />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleClose}>
                  {error ? 'Fechar' : 'Cancelar'}
                </CancelButton>
                {!error && (
                  <SaveButton type="submit" onClick={() => formik.handleSubmit(formik.values)} disabled={disabled}>
                    Processar
                  </SaveButton>
                )}
              </>
            ),
          }}
          content={
            <>
              {error ? (
                <ImportErrorTable errors={errorData} />
              ) : (
                <ImportModal
                  onFileChange={onFileChange}
                  arquivo={arquivo}
                  obterModelo={obterModelo}
                  progress={progress}
                  handleProgressUpdate={handleProgressUpdate}
                  formik={formik}
                />
              )}
            </>
          }
          contentStyle={{ padding: 0 }}
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default ImportarClienteModal;
